import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, Link2Off, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CopyToClipboard } from "@/components/ui/copy-to-clipboard";
import { useLocale } from "@/hooks/useLocale";

export interface ShareLinkData {
  token: string;
  url: string;
  isActive: boolean;
  expiresAt: string | null;
}

interface ShareChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationTitle?: string;
  existing: ShareLinkData | null;
  isLoading?: boolean;
  isCreating?: boolean;
  isRevoking?: boolean;
  onCreate: (expiresInDays: number | null) => Promise<ShareLinkData | null>;
  onRevoke: () => Promise<void>;
}

/** `value` is the API contract (days); `label` is a lookup key. */
const EXPIRY_OPTIONS: { label: string; value: number | null }[] = [
  { label: "company:employees.never", value: null },
  { label: "common:ai_assistant.expiry_1_day", value: 1 },
  { label: "common:ai_assistant.expiry_7_days", value: 7 },
  { label: "common:ai_assistant.expiry_30_days", value: 30 },
];

export const ShareChatDialog = ({
  open,
  onOpenChange,
  conversationTitle,
  existing,
  isLoading,
  isCreating,
  isRevoking,
  onCreate,
  onRevoke,
}: ShareChatDialogProps) => {
  const { t } = useTranslation(["common", "company"]);
  const { formatDate } = useLocale();
  const [expiresInDays, setExpiresInDays] = useState<number | null>(null);
  const [link, setLink] = useState<ShareLinkData | null>(existing);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  useEffect(() => {
    setLink(existing);
    setConfirmRevoke(false);
  }, [existing, open]);

  const handleCreate = async () => {
    try {
      const created = await onCreate(expiresInDays);
      if (created) {
        setLink(created);
        toast.success(t("common:ai_assistant.public_link_created"));
      }
    } catch (err: any) {
      toast.error(
        err?.message ??
          err?.data?.message ??
          t("common:ai_assistant.failed_to_create_link"),
      );
    }
  };

  const handleRevoke = async () => {
    try {
      await onRevoke();
      setLink(null);
      setConfirmRevoke(false);
      toast.success(t("common:ai_assistant.shared_link_revoked"));
    } catch (err: any) {
      toast.error(
        err?.message ??
          err?.data?.message ??
          t("common:ai_assistant.failed_to_revoke"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t("common:ai_assistant.share_public_link_title")}</DialogTitle>
          <DialogDescription>
            {link
              ? t("common:ai_assistant.share_link_active_description")
              : t("common:ai_assistant.share_link_create_description")}
          </DialogDescription>
        </DialogHeader>

        {conversationTitle && (
          <div className="text-sm text-slate-600 truncate">
            <Trans
              t={t}
              i18nKey="common:ai_assistant.chat_title_label"
              values={{ title: conversationTitle }}
              components={{ muted: <span className="text-slate-400" /> }}
            />
          </div>
        )}

        {isLoading ? (
          <div className="py-6 flex items-center justify-center text-slate-400">
            <Loader2 className="animate-spin" />
          </div>
        ) : link ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded border bg-slate-50 px-2 py-1.5">
              <Input
                readOnly
                value={link.url}
                className="border-0 bg-transparent shadow-none focus-visible:ring-0 px-0 text-xs"
              />
              <CopyToClipboard value={link.url} size="sm" />
            </div>
            {link.expiresAt && (
              <p className="text-xs text-slate-500">
                {t("common:ai_assistant.link_expires_on", {
                  date: formatDate(new Date(link.expiresAt), "Pp"),
                })}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <label className="text-xs text-slate-500">
              {t("common:ai_assistant.link_expiry")}
            </label>
            <div className="flex flex-wrap gap-2">
              {EXPIRY_OPTIONS.map((opt) => (
                <button
                  type="button"
                  key={String(opt.value)}
                  onClick={() => setExpiresInDays(opt.value)}
                  className={`px-3 py-1 rounded-full text-xs border ${
                    expiresInDays === opt.value
                      ? "border-primary text-primary bg-primary/10"
                      : "border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                >
                  {t(opt.label)}
                </button>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="mt-2 flex gap-2">
          {link ? (
            confirmRevoke ? (
              <div className="flex w-full flex-col gap-2">
                <p className="text-xs text-danger">
                  {t("common:ai_assistant.revoke_link_confirm")}
                </p>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setConfirmRevoke(false)}
                    disabled={isRevoking}
                  >
                    {t("common:buttons.cancel")}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleRevoke}
                    isLoading={isRevoking}
                  >
                    <Link2Off className="size-4" /> {t("common:ai_assistant.revoke_link")}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setConfirmRevoke(true)}
                  disabled={isRevoking}
                >
                  <Link2Off className="size-4" /> {t("common:ai_assistant.revoke_link")}
                </Button>
                <Button onClick={handleCreate} isLoading={isCreating}>
                  <Link className="size-4" /> {t("common:ai_assistant.update_link")}
                </Button>
              </>
            )
          ) : (
            <Button onClick={handleCreate} isLoading={isCreating}>
              <Link className="size-4" /> {t("common:ai_assistant.create_link")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
